import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilePlus2, AlertTriangle, Image, Search } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { routeSummary, firstDate, fmtDateTime } from "@/lib/helpers";
import { useOnNotify } from "@/components/admin/NotificationBell";

const FILTERS = [
  { key: "all", label: "Toate", match: () => true },
  { key: "new", label: "Neatribuite", match: (o) => o.status === "new" },
  { key: "progress", label: "În curs", match: (o) => !["new", "unloaded"].includes(o.status) },
  { key: "done", label: "Finalizate", match: (o) => o.status === "unloaded" },
  { key: "issues", label: "Cu probleme", match: (o) => o.open_issues > 0 },
];

function Stat({ label, value, accent, testId }) {
  return (
    <div className="panel fade-up" data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 font-display text-4xl font-bold" style={{ color: accent }}>{value ?? "–"}</div>
    </div>
  );
}

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const refresh = useCallback(() => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);
  useOnNotify(refresh);

  useEffect(() => {
    refresh();
    api.get("/drivers").then((r) => setDrivers(Object.fromEntries(r.data.map((d) => [d.id, d]))));
  }, [refresh]);

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    const s = q.trim().toLowerCase();
    return orders.filter(f.match).filter((o) => !s || JSON.stringify([o.reference, o.partner, o.stops, drivers[o.driver_id]?.name]).toLowerCase().includes(s));
  }, [orders, filter, q, drivers]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Dispecerat</div>
          <h1 className="mt-1 font-display text-4xl font-extrabold uppercase sm:text-5xl">Comenzi</h1>
        </div>
        <Link to="/admin/orders/new" data-testid="dashboard-new-order-btn" className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-blue-700">
          <FilePlus2 className="h-4 w-4" /> Încarcă PDF comandă
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Neatribuite" value={stats.new} accent="#94A3B8" testId="stat-new" />
        <Stat label="În curs" value={stats.in_progress} accent="#60A5FA" testId="stat-progress" />
        <Stat label="Finalizate" value={stats.done} accent="#10B981" testId="stat-done" />
        <Stat label="Probleme deschise" value={stats.open_issues} accent="#EF4444" testId="stat-issues" />
        <Stat label="Șoferi activi" value={stats.drivers} accent="#E2E8F0" testId="stat-drivers" />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-full border border-white/10 bg-black/30 p-1">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${filter === f.key ? "bg-[#1E293B] text-white" : "text-slate-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="orders-search-input" placeholder="Caută referință, oraș, șofer..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-white/10 bg-[#111827]">
        <table className="w-full min-w-[760px] text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {["Referință", "Traseu", "Încărcare", "Șofer", "Status", "Docs", "Creată"].map((h) => (
                <th key={h} className="eyebrow px-4 py-3 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => (
              <tr key={o.id} onClick={() => nav(`/admin/orders/${o.id}`)} data-testid={`order-row-${o.id}`}
                className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]">
                <td className="px-4 py-3 font-mono text-xs">{o.reference || "—"}</td>
                <td className="px-4 py-3 font-semibold">{routeSummary(o.stops)}</td>
                <td className="px-4 py-3 text-slate-400">{firstDate(o.stops)}</td>
                <td className="px-4 py-3">{drivers[o.driver_id]?.name || <span className="text-slate-500">neatribuit</span>}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} testId={`order-status-${o.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 text-slate-400">
                    <span className="inline-flex items-center gap-1"><Image className="h-3.5 w-3.5" />{o.doc_count}</span>
                    {o.open_issues > 0 && <span className="inline-flex items-center gap-1 text-red-400"><AlertTriangle className="h-3.5 w-3.5" />{o.open_issues}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{fmtDateTime(o.created_at)}</td>
              </tr>
            ))}
            {!shown.length && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500" data-testid="orders-empty">Nicio comandă aici încă. Încarcă primul PDF de la bursă.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
